"use client";

import clsx from "clsx";
import { isPublic } from "@/lib/profile";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * PrivateOnly markers — visual cues for content that exists in the private
 * demo build but is stripped from the public build via Phase 3 isPublic()
 * guards. All exports are no-ops on the public profile, so they can be
 * dropped in anywhere without affecting public.
 *
 *   <PrivateOnlyMark>           — wraps a block (panel, column, side-nav
 *                                 tile) in an amber-tinted box with a
 *                                 "🛑" corner chip.
 *   <PrivateOnlyMark inline>    — emits a small inline 🛑 chip before the
 *                                 children. For phrases inside a paragraph.
 *   <PrivateBadge />            — standalone 🛑 chip; drop inline next to
 *                                 a row / chip / tile heading. Use together
 *                                 with PRIVATE_EDGE_CLASSES for the amber
 *                                 gutter mark on the host element.
 *   PRIVATE_EDGE_CLASSES        — Tailwind class string adding an amber
 *                                 left-border to an existing element.
 */

// Tight circular chip showing just the 🛑 glyph. Amber-tinted to match the
// rest of the private-only system; hover-titled "Hidden on public demo".
const PRIVATE_CHIP =
  "inline-flex h-4 w-4 items-center justify-center rounded-full border border-state-financed/40 bg-state-financed/15 text-[10px] leading-none text-state-financed";

export const PRIVATE_EDGE_CLASSES = "border-s-2 border-s-state-financed";

export function PrivateBadge({ className }: { className?: string }) {
  const t = useTranslations("privateOnlyMark");
  if (isPublic()) return null;
  return (
    <span
      aria-label={t("ariaLabel")}
      title={t("titleHint")}
      className={clsx(PRIVATE_CHIP, className)}
    >
      🛑
    </span>
  );
}

export function PrivateOnlyMark({
  children,
  /** Render as a tiny inline chip before children, instead of wrapping. */
  inline = false,
  className,
}: {
  children: React.ReactNode;
  inline?: boolean;
  className?: string;
}) {
  const t = useTranslations("privateOnlyMark");
  if (isPublic()) return <>{children}</>;

  if (inline) {
    return (
      <>
        <span
          aria-label={t("ariaLabel")}
          title={t("titleHint")}
          className={clsx("me-1 align-baseline", PRIVATE_CHIP)}
        >
          🛑
        </span>
        {children}
      </>
    );
  }

  // Block wrap — panels, columns, side-nav tiles, bubbles.
  return (
    <div
      aria-label={t("ariaLabel")}
      title={t("titleHint")}
      className={clsx(
        "relative rounded-md border border-state-financed/35 bg-state-financed/5 p-[2px]",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={clsx(
          "absolute end-2 top-2 z-10 shadow-[0_0_10px_var(--state-financed-glow)]",
          PRIVATE_CHIP,
        )}
      >
        🛑
      </span>
      {children}
    </div>
  );
}
